import { PRESENTATION_CHART_TYPES, PRESENTATION_SLIDE_KINDS } from './constants';

const CHART_TYPES = new Set(Object.values(PRESENTATION_CHART_TYPES));
const SLIDE_KINDS = new Set(Object.values(PRESENTATION_SLIDE_KINDS));

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const addError = (errors, path, message) => {
  errors.push({ message, path });
};

const validateSeries = ({ categories, errors, path, series }) => {
  if (!Array.isArray(series) || series.length === 0) {
    addError(errors, path, 'must be a non-empty array');
    return;
  }

  series.forEach((item, index) => {
    const seriesPath = `${path}[${index}]`;
    if (!isObject(item)) {
      addError(errors, seriesPath, 'must be an object');
      return;
    }
    if (!isNonEmptyString(item.name)) {
      addError(errors, `${seriesPath}.name`, 'must be a non-empty string');
    }
    if (!Array.isArray(item.values) || item.values.length === 0) {
      addError(errors, `${seriesPath}.values`, 'must be a non-empty array');
    } else if (Array.isArray(categories) && item.values.length !== categories.length) {
      addError(
        errors,
        `${seriesPath}.values`,
        `must contain ${categories.length} values to match categories`
      );
    }
  });
};

const validateCardsSlide = ({ errors, path, slide }) => {
  if (!Array.isArray(slide.cards) || slide.cards.length === 0) {
    addError(errors, `${path}.cards`, 'must be a non-empty array');
    return;
  }

  if (slide.columns !== undefined && (!Number.isInteger(slide.columns) || slide.columns < 1)) {
    addError(errors, `${path}.columns`, 'must be a positive integer when provided');
  }

  if (slide.fitOnSingleSlide !== undefined && typeof slide.fitOnSingleSlide !== 'boolean') {
    addError(errors, `${path}.fitOnSingleSlide`, 'must be a boolean when provided');
  }

  slide.cards.forEach((card, index) => {
    const cardPath = `${path}.cards[${index}]`;
    if (!isObject(card)) {
      addError(errors, cardPath, 'must be an object');
      return;
    }
    ['col', 'row'].forEach((key) => {
      if (!Number.isInteger(card[key]) || card[key] < 0) {
        addError(errors, `${cardPath}.${key}`, 'must be a non-negative integer');
      }
    });
    if (!isNonEmptyString(card.title)) {
      addError(errors, `${cardPath}.title`, 'must be a non-empty string');
    }
    if (!isNonEmptyString(card.color)) {
      addError(errors, `${cardPath}.color`, 'must be a non-empty string');
    }
    if (!Array.isArray(card.rows)) {
      addError(errors, `${cardPath}.rows`, 'must be an array');
    } else {
      card.rows.forEach((row, rowIndex) => {
        const rowPath = `${cardPath}.rows[${rowIndex}]`;
        if (!isObject(row)) {
          addError(errors, rowPath, 'must be an object');
          return;
        }
        ['label', 'value'].forEach((key) => {
          if (typeof row[key] !== 'string') {
            addError(errors, `${rowPath}.${key}`, 'must be a string');
          }
        });
      });
    }
    if (Number.isInteger(slide.columns) && card.col >= slide.columns) {
      addError(errors, `${cardPath}.col`, 'must be less than columns');
    }
  });
};

const validateChartSlide = ({ errors, path, slide }) => {
  if (!CHART_TYPES.has(slide.chartType)) {
    addError(errors, `${path}.chartType`, `must be one of: ${Array.from(CHART_TYPES).join(', ')}`);
  }
  const isScatter = slide.chartType === PRESENTATION_CHART_TYPES.SCATTER;
  if (
    (!isScatter && (!Array.isArray(slide.categories) || slide.categories.length === 0)) ||
    (isScatter && slide.categories !== undefined && !Array.isArray(slide.categories))
  ) {
    addError(
      errors,
      `${path}.categories`,
      isScatter ? 'must be an array when provided' : 'must be a non-empty array'
    );
  }
  validateSeries({
    categories: isScatter ? undefined : slide.categories,
    errors,
    path: `${path}.series`,
    series: slide.series,
  });

  if (slide.stacking !== undefined && !['normal', 'percent'].includes(slide.stacking)) {
    addError(errors, `${path}.stacking`, 'must be normal or percent when provided');
  }

  if (slide.comparisonValues !== undefined) {
    if (
      !Array.isArray(slide.comparisonValues) ||
      slide.comparisonValues.length !== slide.categories?.length
    ) {
      addError(
        errors,
        `${path}.comparisonValues`,
        'must contain one comparison value for each category'
      );
    } else {
      slide.comparisonValues.forEach((comparison, index) => {
        const comparisonPath = `${path}.comparisonValues[${index}]`;
        if (!isObject(comparison)) {
          addError(errors, comparisonPath, 'must be an object');
          return;
        }
        ['delta', 'value'].forEach((key) => {
          if (
            comparison[key] !== null &&
            (typeof comparison[key] !== 'number' || !Number.isFinite(comparison[key]))
          ) {
            addError(errors, `${comparisonPath}.${key}`, 'must be a finite number or null');
          }
        });
      });
    }
  }

  if (slide.chartType === PRESENTATION_CHART_TYPES.COMBO) {
    if (slide.series?.length !== 2) {
      addError(errors, `${path}.series`, 'must contain exactly two series for a combo chart');
    }
    if (!Array.isArray(slide.valueFormats) || slide.valueFormats.length !== 2) {
      addError(
        errors,
        `${path}.valueFormats`,
        'must contain exactly two formats for a combo chart'
      );
    }
  } else if (slide.valueFormat !== undefined && !isNonEmptyString(slide.valueFormat)) {
    addError(errors, `${path}.valueFormat`, 'must be a non-empty string when provided');
  }

  if (isScatter) {
    if (slide.xValueFormat !== undefined && !isNonEmptyString(slide.xValueFormat)) {
      addError(errors, `${path}.xValueFormat`, 'must be a non-empty string when provided');
    }
    ['xAxisTitle', 'yAxisTitle'].forEach((key) => {
      if (!isNonEmptyString(slide[key])) {
        addError(errors, `${path}.${key}`, 'must be a non-empty string');
      }
    });
    slide.series?.forEach((series, seriesIndex) => {
      series?.values?.forEach((point, pointIndex) => {
        const pointPath = `${path}.series[${seriesIndex}].values[${pointIndex}]`;
        if (!isObject(point)) {
          addError(errors, pointPath, 'must be an object');
          return;
        }
        ['x', 'y'].forEach((key) => {
          if (typeof point[key] !== 'number' || !Number.isFinite(point[key])) {
            addError(errors, `${pointPath}.${key}`, 'must be a finite number');
          }
        });
      });
    });
  } else {
    slide.series?.forEach((series, seriesIndex) => {
      series?.values?.forEach((value, valueIndex) => {
        if (value !== null && (typeof value !== 'number' || !Number.isFinite(value))) {
          addError(
            errors,
            `${path}.series[${seriesIndex}].values[${valueIndex}]`,
            'must be a finite number or null'
          );
        }
      });
    });
  }
};

const validateImageSlide = ({ errors, path, slide }) => {
  if (!isNonEmptyString(slide.data)) {
    addError(errors, `${path}.data`, 'must be a non-empty image data string');
  }
  if (!isObject(slide.placement)) {
    addError(errors, `${path}.placement`, 'must be an object');
  } else {
    ['h', 'w', 'x', 'y'].forEach((key) => {
      if (slide.placement[key] === undefined) {
        addError(errors, `${path}.placement.${key}`, 'is required');
      }
    });
  }
};

const validateMetadataSlide = ({ errors, path, slide }) => {
  if (!Array.isArray(slide.entries) || slide.entries.length === 0) {
    addError(errors, `${path}.entries`, 'must be a non-empty array');
    return;
  }

  slide.entries.forEach((entry, index) => {
    const entryPath = `${path}.entries[${index}]`;
    if (!isObject(entry)) {
      addError(errors, entryPath, 'must be an object');
      return;
    }
    if (!isNonEmptyString(entry.label)) {
      addError(errors, `${entryPath}.label`, 'must be a non-empty string');
    }
    if (entry.value === undefined || entry.value === null) {
      addError(errors, `${entryPath}.value`, 'is required');
    }
  });
};

const validateTableSlide = ({ errors, path, slide }) => {
  if (!Array.isArray(slide.columns) || slide.columns.length < 2) {
    addError(errors, `${path}.columns`, 'must contain at least two columns');
  } else {
    slide.columns.forEach((column, index) => {
      const columnPath = `${path}.columns[${index}]`;
      if (!isObject(column)) {
        addError(errors, columnPath, 'must be an object');
        return;
      }
      if (!isNonEmptyString(column.key)) {
        addError(errors, `${columnPath}.key`, 'must be a non-empty string');
      }
      if (!isNonEmptyString(column.header)) {
        addError(errors, `${columnPath}.header`, 'must be a non-empty string');
      }
    });
  }

  if (!Array.isArray(slide.rows)) {
    addError(errors, `${path}.rows`, 'must be an array');
  } else {
    slide.rows.forEach((row, index) => {
      if (!isObject(row)) {
        addError(errors, `${path}.rows[${index}]`, 'must be an object');
      }
    });
  }
};

const validateTextSlide = ({ errors, path, slide }) => {
  const hasBody = isNonEmptyString(slide.body);
  const hasRuns = Array.isArray(slide.runs) && slide.runs.length > 0;
  if (!hasBody && !hasRuns) {
    addError(errors, path, 'must provide a non-empty body or runs array');
  }
  if (hasRuns) {
    slide.runs.forEach((run, index) => {
      if (!isObject(run) || !isNonEmptyString(run.text)) {
        addError(errors, `${path}.runs[${index}].text`, 'must be a non-empty string');
      }
    });
  }
};

const validateTreeSlide = ({ errors, path, slide }) => {
  if (!Array.isArray(slide.nodes) || slide.nodes.length === 0) {
    addError(errors, `${path}.nodes`, 'must be a non-empty array');
    return;
  }

  slide.nodes.forEach((node, index) => {
    const nodePath = `${path}.nodes[${index}]`;
    if (!isObject(node)) {
      addError(errors, nodePath, 'must be an object');
      return;
    }
    if (!isNonEmptyString(node.id)) {
      addError(errors, `${nodePath}.id`, 'must be a non-empty string');
    }
    ['col', 'gridSpan', 'row'].forEach((key) => {
      if (typeof node[key] !== 'number' || !Number.isFinite(node[key])) {
        addError(errors, `${nodePath}.${key}`, 'must be a finite number');
      }
    });
    if (Number.isFinite(node.col) && node.col < 0) {
      addError(errors, `${nodePath}.col`, 'must be zero or greater');
    }
    if (Number.isFinite(node.gridSpan) && node.gridSpan <= 0) {
      addError(errors, `${nodePath}.gridSpan`, 'must be greater than zero');
    }
    if (Number.isFinite(node.row) && node.row < 1) {
      addError(errors, `${nodePath}.row`, 'must be one or greater');
    }
    if (!isNonEmptyString(node.title)) {
      addError(errors, `${nodePath}.title`, 'must be a non-empty string');
    }
    if (!isNonEmptyString(node.color)) {
      addError(errors, `${nodePath}.color`, 'must be a non-empty string');
    }
    if (!Array.isArray(node.rows)) {
      addError(errors, `${nodePath}.rows`, 'must be an array');
    } else {
      node.rows.forEach((row, rowIndex) => {
        const rowPath = `${nodePath}.rows[${rowIndex}]`;
        if (!isObject(row)) {
          addError(errors, rowPath, 'must be an object');
          return;
        }
        ['left', 'right'].forEach((key) => {
          if (typeof row[key] !== 'string') {
            addError(errors, `${rowPath}.${key}`, 'must be a string');
          }
        });
      });
    }
  });
};

const validateTornadoSlide = ({ errors, path, slide }) => {
  if (!Array.isArray(slide.categories) || slide.categories.length === 0) {
    addError(errors, `${path}.categories`, 'must be a non-empty array');
  }
  validateSeries({
    categories: slide.categories,
    errors,
    path: `${path}.series`,
    series: slide.series,
  });
  if (slide.series?.length !== 3) {
    addError(errors, `${path}.series`, 'must contain gains, losses, and net-change series');
  }
  slide.series?.forEach((series, seriesIndex) => {
    series?.values?.forEach((value, valueIndex) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        addError(
          errors,
          `${path}.series[${seriesIndex}].values[${valueIndex}]`,
          'must be a finite number'
        );
      }
    });
  });
  if (slide.valueFormat !== undefined && !isNonEmptyString(slide.valueFormat)) {
    addError(errors, `${path}.valueFormat`, 'must be a non-empty string when provided');
  }
};

const validateWaterfallSlide = ({ errors, path, slide }) => {
  if (!Array.isArray(slide.categories) || slide.categories.length === 0) {
    addError(errors, `${path}.categories`, 'must be a non-empty array');
  }
  validateSeries({
    categories: slide.categories,
    errors,
    path: `${path}.series`,
    series: slide.series,
  });
  if (slide.series?.length !== 4) {
    addError(errors, `${path}.series`, 'must contain exactly four waterfall stack series');
  }
  if (!Array.isArray(slide.labels) || slide.labels.length !== slide.categories?.length) {
    addError(errors, `${path}.labels`, 'must contain one label for each category');
  }
};

const SLIDE_VALIDATORS = {
  [PRESENTATION_SLIDE_KINDS.CARDS]: validateCardsSlide,
  [PRESENTATION_SLIDE_KINDS.CHART]: validateChartSlide,
  [PRESENTATION_SLIDE_KINDS.IMAGE]: validateImageSlide,
  [PRESENTATION_SLIDE_KINDS.METADATA]: validateMetadataSlide,
  [PRESENTATION_SLIDE_KINDS.TABLE]: validateTableSlide,
  [PRESENTATION_SLIDE_KINDS.TAKEAWAYS]: validateTextSlide,
  [PRESENTATION_SLIDE_KINDS.TEXT]: validateTextSlide,
  [PRESENTATION_SLIDE_KINDS.TORNADO]: validateTornadoSlide,
  [PRESENTATION_SLIDE_KINDS.TREE]: validateTreeSlide,
  [PRESENTATION_SLIDE_KINDS.WATERFALL]: validateWaterfallSlide,
};

const validateFooter = ({ errors, footer }) => {
  if (footer === undefined) return;
  if (!isObject(footer)) {
    addError(errors, 'footer', 'must be an object');
    return;
  }

  if (footer.dateRange !== undefined) {
    const isValidDateRange =
      Array.isArray(footer.dateRange) &&
      footer.dateRange.length === 2 &&
      footer.dateRange.every(isNonEmptyString);
    if (!isValidDateRange) {
      addError(errors, 'footer.dateRange', 'must contain exactly two non-empty strings');
    }
  }

  ['sourceLabel', 'sourceUrl'].forEach((key) => {
    if (footer[key] !== undefined && !isNonEmptyString(footer[key])) {
      addError(errors, `footer.${key}`, 'must be a non-empty string when provided');
    }
  });

  if (footer.attributes !== undefined && !isObject(footer.attributes)) {
    addError(errors, 'footer.attributes', 'must be an object when provided');
  }
};

const validateSlide = ({ errors, path, slide }) => {
  if (!isObject(slide)) {
    addError(errors, path, 'must be an object');
    return;
  }
  if (!SLIDE_KINDS.has(slide.kind)) {
    addError(errors, `${path}.kind`, `must be one of: ${Array.from(SLIDE_KINDS).join(', ')}`);
    return;
  }
  if (!isNonEmptyString(slide.title)) {
    addError(errors, `${path}.title`, 'must be a non-empty string');
  }
  if (slide.insight !== undefined && !isNonEmptyString(slide.insight)) {
    addError(errors, `${path}.insight`, 'must be a non-empty string when provided');
  }

  SLIDE_VALIDATORS[slide.kind]({ errors, path, slide });
};

const validateSections = ({ errors, sections }) => {
  if (!Array.isArray(sections) || sections.length === 0) {
    addError(errors, 'sections', 'must be a non-empty array');
    return;
  }

  const sectionIds = new Set();
  sections.forEach((section, index) => {
    const path = `sections[${index}]`;
    if (!isObject(section)) {
      addError(errors, path, 'must be an object');
      return;
    }
    if (!isNonEmptyString(section.id)) {
      addError(errors, `${path}.id`, 'must be a non-empty string');
    } else if (sectionIds.has(section.id)) {
      addError(errors, `${path}.id`, 'must be unique');
    } else {
      sectionIds.add(section.id);
    }
    if (section.title !== undefined && !isNonEmptyString(section.title)) {
      addError(errors, `${path}.title`, 'must be a non-empty string when provided');
    }
    if (!Array.isArray(section.slides) || section.slides.length === 0) {
      addError(errors, `${path}.slides`, 'must be a non-empty array');
      return;
    }
    section.slides.forEach((slide, slideIndex) =>
      validateSlide({ errors, path: `${path}.slides[${slideIndex}]`, slide })
    );
  });
};

/**
 * Validates the canonical, feature-agnostic presentation model.
 *
 * @param {Object} presentation
 * @returns {{ errors: Array<{message: String, path: String}>, isValid: Boolean }}
 */
export const validatePresentation = (presentation) => {
  const errors = [];
  if (!isObject(presentation)) {
    addError(errors, 'presentation', 'must be an object');
    return { errors, isValid: false };
  }

  if (!isNonEmptyString(presentation.fileName)) {
    addError(errors, 'fileName', 'must be a non-empty string');
  }
  if (!isObject(presentation.title)) {
    addError(errors, 'title', 'must be an object');
  } else {
    if (!isNonEmptyString(presentation.title.heading)) {
      addError(errors, 'title.heading', 'must be a non-empty string');
    }
    if (!isNonEmptyString(presentation.title.subtitle)) {
      addError(errors, 'title.subtitle', 'must be a non-empty string');
    }
  }

  validateFooter({ errors, footer: presentation.footer });
  validateSections({ errors, sections: presentation.sections });

  return { errors, isValid: errors.length === 0 };
};

export class PresentationValidationError extends Error {
  constructor(errors) {
    super(
      `Invalid PowerPoint presentation:\n${errors
        .map(({ message, path }) => `- ${path}: ${message}`)
        .join('\n')}`
    );
    this.errors = errors;
    this.name = 'PresentationValidationError';
  }
}

/**
 * Throws a path-aware PresentationValidationError when the model is malformed; otherwise returns the
 * original model so it can be used inline at the renderer boundary.
 *
 * @param {Object} presentation
 * @returns {Object}
 */
export const assertValidPresentation = (presentation) => {
  const result = validatePresentation(presentation);
  if (!result.isValid) throw new PresentationValidationError(result.errors);
  return presentation;
};
